import { normalizeId } from './ids';
import { earliestDateValue } from './dateOptions';
import { getFieldValue } from './sort';
import { withoutHiddenElements } from './elementAccess';
import { getCardScope } from './cardScopes';
import { collectionCardPath } from '../config/collectionShell';
import { ancestorsOf } from './collectionFamily';

// Own-property reads wherever an id indexes a plain object: a card id can
// never be `toString`, but an index built from user data must not find
// Object.prototype either.
const hasOwn = (obj, key) => !!obj && Object.prototype.hasOwnProperty.call(obj, key);
const elementsOf = card => (Array.isArray(card?.elements) ? card.elements : []);

// ---- Element sets (docs/plans/element-sets-and-me.md) ---------------------
//
// Beside the hierarchy — which says what a card IS — a card may ATTACH
// element sets (`cardDefinitions.elementSets`, ordered ids), which say what
// it HAS: a reusable bundle whose elements land on items exactly like
// inherited ones, flat under `elements.<name>` (the name already namespaced
// `<setKey>_<slug>` by the API). The effective list per chain node is
// `[...own, ...each attached set's elements in elementSets[] order]`, and
// descendants inherit an ancestor's sets — the API's resolver
// (keepr-api utils/elementSetResolve.js) verbatim, so what the form renders
// is what the write path stores. Set elements are COPIES stamped with
// `options.group = <set name>` (when the element names no group: a set
// renders as one panel named for the set) and `fromSet: { id, key, name }`,
// the provenance a card home reads as "from Contact". Cached documents are
// never mutated.

/** Index a list of element sets by normalized id (the indexCardsById twin). */
/**
 * A card's page. `card` is the card's document, or its id when that is all
 * the link has.
 *
 * A collection card's page is in the collection that owns it,
 * `/collections/{owner}/cards/:id` (KPR-1283, KPE-039), whatever collection
 * the link is drawn in: a parent's card seen from a sub-collection opens in
 * the parent.
 *
 * From inside a collection a GLOBAL card opens in that collection too,
 * `/collections/{collectionId}/cards/:id` (KPR-1285): its home, with the
 * collection's own settings for it. So does an id alone, since the page there
 * judges the card once it has loaded (CardPageFrame `cardShellRedirect`): a
 * collection card goes on to its owner, a global card the collection does not
 * hold to `/cards/:id`. An unassigned, legacy or ownerless card keeps the
 * platform page, saying which collection the link came from with
 * `?collectionId=` (the tile layout's collection tiers need one in hand).
 * Without a collection, anything but a collection card is `/cards/:id`.
 */
export function cardPagePath(card, collectionId = null, query = '') {
	const doc = card && typeof card === 'object' && !card.$oid ? card : null;
	const cardId = doc ? normalizeId(doc._id || doc.id) : normalizeId(card);
	const params = new URLSearchParams(query);
	const owner = doc && getCardScope(doc) === 'collection' ? normalizeId(doc.collection_id) : null;
	const cid = collectionId ? normalizeId(collectionId) : null;
	const home = owner || ((!doc || getCardScope(doc) === 'global') ? cid : null);
	if (home) {
		params.delete('collectionId');
		const search = params.toString();
		return `${collectionCardPath(home, cardId)}${search ? `?${search}` : ''}`;
	}
	if (cid) params.set('collectionId', cid);
	const search = params.toString();
	return `/cards/${cardId}${search ? `?${search}` : ''}`;
}

/**
 * A Card Library row's page (KPR-1285). A card from above, listed under
 * From {parent} in a sub-collection, opens in the collection it comes from:
 * its owner for an ancestor's own card, the parent for a global card the
 * parent holds. When this person cannot open that collection (their access is
 * on the sub-collection alone), it opens here instead, where they use it,
 * rather than on a collection that answers "not found". Any other row is
 * `cardPagePath` with this collection.
 */
export function libraryCardPath(row, collection, query = '') {
	const collectionId = normalizeId(collection?._id);
	if (!row?.inherited) return cardPagePath(row?.def || row?.id, collectionId, query);
	const cardId = normalizeId(row.def?._id || row.id);
	const owner = row.def && getCardScope(row.def) === 'collection' ? normalizeId(row.def.collection_id) : null;
	const ancestors = ancestorsOf(collection);
	const from = owner || ancestors[ancestors.length - 1]?._id || null;
	const closed = !from || ancestors.some(ancestor => ancestor._id === from && !ancestor.readable);
	const search = new URLSearchParams(query).toString();
	return `${collectionCardPath(closed ? collectionId : from, cardId)}${search ? `?${search}` : ''}`;
}

export function indexElementSetsById(sets = []) {
	return Object.fromEntries(
		(sets || [])
			.map(set => [normalizeId(set?._id), set])
			.filter(([id]) => Boolean(id))
	);
}

/** The normalized id strings of a card's `elementSets` (absent = []), in order, garbage dropped. */
export function elementSetIdsOf(card) {
	const raw = Array.isArray(card?.elementSets) ? card.elementSets : [];
	return raw.map(normalizeId).filter(Boolean);
}

/** The set's elements as a card sees them: copies, the set's group and provenance stamped. */
export function setElementsOf(set) {
	const id = normalizeId(set?._id);
	const name = (set?.name || '').trim();
	const fromSet = { id, key: set?.key || null, name };
	return elementsOf(set).filter(Boolean).map(element => {
		const options = { ...(element.options || {}) };
		if (name && !(typeof options.group === 'string' && options.group.trim())) options.group = name;
		return { ...element, options, fromSet };
	});
}

/** The set documents a card attaches, resolved through the index, in `elementSets[]` order (a missing id contributes nothing). */
export function elementSetDocsOf(card, elementSetsById = null) {
	if (!elementSetsById) return [];
	return elementSetIdsOf(card)
		.map(id => (hasOwn(elementSetsById, id) ? elementSetsById[id] : undefined))
		.filter(Boolean);
}

/**
 * One resolved chain node: the stored document's keys plus
 *   elements        the effective list (own, then each set's, in order)
 *   ownElements     the stored `elements`, untouched
 *   elementSetDocs  the resolved set documents, in `elementSets[]` order
 * A card that attaches no resolvable set is returned AS IS (same identity),
 * so a chain of plain cards is the chain it always was.
 */
export function resolveCardNode(card, elementSetsById = null) {
	if (!card) return card;
	const sets = elementSetDocsOf(card, elementSetsById);
	if (!sets.length) return card;
	const own = elementsOf(card);
	return {
		...card,
		ownElements: own,
		elementSetDocs: sets,
		elements: [...own, ...sets.flatMap(setElementsOf)],
	};
}

/** A parent-first chain with every node's element sets folded in. */
export function resolveChain(chain = [], elementSetsById = null) {
	return (Array.isArray(chain) ? chain : []).map(card => resolveCardNode(card, elementSetsById));
}

/**
 * Every distinct element set a parent-first chain carries, root-first and
 * first-seen, each with the chain node that ATTACHES it — so a card editor
 * can list an ancestor's set muted, "from Person", beside its own.
 * Returns [{ set, card }].
 */
export function chainElementSets(chain = [], elementSetsById = null) {
	const out = [];
	const seen = new Set();
	(Array.isArray(chain) ? chain : []).forEach(card => {
		const sets = Array.isArray(card?.elementSetDocs) ? card.elementSetDocs : elementSetDocsOf(card, elementSetsById);
		sets.forEach(set => {
			const id = normalizeId(set?._id);
			if (!id || seen.has(id)) return;
			seen.add(id);
			out.push({ set, card });
		});
	});
	return out;
}

// ---- THE merge rule -------------------------------------------------------
//
// A card's elements are its ancestors' then its own, merged by NAME with the
// LATER definition winning while the element keeps the EARLIER position:
// the API's write path (keepr-api utils/cardSchemaUtils.js flattenCardElements,
// itemUtils' merged definition) and every formula surface read a child card's
// re-declaration of an inherited name as the child says, in the slot the
// ancestor gave it, so the list order stays stable for a table or a form
// whichever card in the chain declared each element. Every web surface that
// merges a chain goes through here, so nothing renders one way and stores
// the other.

/**
 * Merge a parent-first chain of cards into `{ element, card, set }` entries —
 * `card` is the chain node that supplied the WINNING definition, so a card
 * home can say "from Person" only when the ancestor's definition is the one
 * an item actually gets, and `set` the element set it came from (a resolved
 * node's set elements carry `fromSet`), so it can say "from Contact".
 */
export function mergeChainEntries(chain = []) {
	const byName = new Map();
	(Array.isArray(chain) ? chain : []).forEach(card => {
		elementsOf(card).forEach(element => {
			if (!element?.name) return;
			// Map.set on an existing key keeps insertion order: descendant
			// definition, ancestor position. `set` is the element set that
			// supplied the definition (the `fromSet` stamp of a resolved
			// node's set element), null for a card's own element.
			byName.set(element.name, { element, card, set: element.fromSet || null });
		});
	});
	return [...byName.values()];
}

/** The elements of a parent-first chain, merged by the rule above. */
export function mergeChainElements(chain = []) {
	return mergeChainEntries(chain).map(entry => entry.element);
}

/**
 * Inherited elements then own, merged by the rule above — the two-list shape
 * the card editor needs (the parent chain's resolved elements plus its
 * unsaved form state).
 */
export function mergeElements(inherited, own) {
	return mergeChainElements([{ elements: inherited }, { elements: own }]);
}

/**
 * Build a memoized resolver that returns the full element list for a card,
 * including elements inherited from its parent chain, merged by the rule
 * above: parent elements first, a child's re-declaration of a name winning
 * in the ancestor's position. With `elementSetsById`, every node's attached
 * element sets are folded in after its own elements (see Element sets
 * above). Cycles in the parent chain are guarded by the per-resolver cache.
 * An unknown card answers [].
 *
 * An element whose values an element rule hides from this person
 * (`access: 'hidden'`, served only to someone who edits the card — KPR-509,
 * utils/elementAccess) is left out of the answer: this resolver feeds the
 * item surfaces (forms, tables, tiles, the read view), where the values
 * never arrive and an empty field would read as "not set". The card editor
 * reads its own definition directly and keeps every element it must send
 * back. The merge runs on the whole chain first, so a child's re-declaration
 * hidden from the person drops the element rather than resurrecting the
 * ancestor's definition of it.
 */
export function createElementResolver(cardsById = {}, elementSetsById = null) {
	const cache = new Map();
	const visible = new Map();
	// Every element, hidden ones included: what a chain merges over.
	const resolveAll = rawId => {
		const id = normalizeId(rawId);
		if (!id) return [];
		if (cache.has(id)) return cache.get(id);
		cache.set(id, []); // guards against parent cycles while resolving
		const card = hasOwn(cardsById, id) ? cardsById[id] : undefined;
		if (!card) return [];
		// Each node contributes its own elements, then its attached sets'
		// (resolveCardNode) — the API's effective-definition rule.
		const node = resolveCardNode(card, elementSetsById);
		const merged = mergeElements(card.parentCardId ? resolveAll(card.parentCardId) : [], node.elements);
		cache.set(id, merged);
		return merged;
	};
	const resolver = rawId => {
		const id = normalizeId(rawId);
		if (!id) return [];
		if (!visible.has(id)) visible.set(id, withoutHiddenElements(resolveAll(id)));
		return visible.get(id);
	};
	return resolver;
}

/** The longest a Notes value may be, the API's own cap (items.notes). */
export const NOTES_MAX_LENGTH = 20000;

/**
 * The card's OWN answer about Notes, or undefined when it says nothing.
 * `options.notes` absent means the card inherits — and, with nothing to
 * inherit from, means ON (docs/plans/data-types.md decision 9).
 */
export function cardNotesSetting(card) {
	const value = card?.options?.notes;
	return typeof value === 'boolean' ? value : undefined;
}

/**
 * Is Notes on for this card, given its ancestry? `chain` is parent-first
 * (the shape the item form loads) and ends with the card itself. The most
 * SPECIFIC explicit setting wins — a child that says `notes: false` under a
 * parent that says nothing (or true) is off — and a chain that says nothing
 * anywhere is on.
 */
export function notesEnabledForChain(chain = []) {
	for (let i = (chain || []).length - 1; i >= 0; i -= 1) {
		const setting = cardNotesSetting(chain[i]);
		if (setting !== undefined) return setting;
	}
	return true;
}

/**
 * The most SPECIFIC explicit boolean in a parent-first `chain` under the first
 * of `keys` a node sets, else `fallback` — the rule every card option follows.
 */
function booleanForChain(chain, keys, fallback) {
	for (let i = (chain || []).length - 1; i >= 0; i -= 1) {
		const options = chain[i]?.options;
		if (!options || typeof options !== 'object') continue;
		for (const key of keys) {
			if (typeof options[key] === 'boolean') return options[key];
		}
	}
	return fallback;
}

/**
 * The card's OWN answer about one comment setting (`allowComments`,
 * `strictCommentAuditing` or `commentsExpanded`), or undefined when it says
 * nothing. `commentsExpanded` falls back to the card's stored
 * `notesExpanded`, its notes-era spelling (KPR-305).
 */
export function cardCommentSetting(card, key) {
	const keys = key === 'commentsExpanded' ? ['commentsExpanded', 'notesExpanded'] : [key];
	const value = booleanForChain(card ? [card] : [], keys, undefined);
	return typeof value === 'boolean' ? value : undefined;
}

/**
 * Do this card's items take comments (`options.allowComments`)? `chain` is
 * parent-first and ends with the card; the most SPECIFIC explicit setting
 * wins, and a chain that says nothing is OFF (KPE-005, the owner's ruling).
 */
export function commentsAllowedForChain(chain = []) {
	return booleanForChain(chain, ['allowComments'], false);
}

/** Does a deleted comment leave a visible row (`options.strictCommentAuditing`)? Same rule, off by default. */
export function strictCommentAuditingForChain(chain = []) {
	return booleanForChain(chain, ['strictCommentAuditing'], false);
}

/**
 * Does the item form open the section unfolded (`options.commentsExpanded`,
 * display only)? Same chain rule, COLLAPSED by default. A node that has not
 * been saved since the rename answers with its stored `notesExpanded`. An item
 * that already has content is expanded whatever this says; the caller applies
 * that.
 */
export function commentsExpandedForChain(chain = []) {
	return booleanForChain(chain, ['commentsExpanded', 'notesExpanded'], false);
}

/**
 * The card's OWN worklist config (`options.worklist`), or undefined when it
 * says nothing. `false` is how a child card turns off a worklist its parent
 * declares — the same three-state rule Notes follows.
 */
export function cardWorklistSetting(card) {
	const value = card?.options?.worklist;
	if (value === undefined || value === null) return undefined;
	return value;
}

/**
 * The worklist in force for a card chain (parent-first, card last): the most
 * SPECIFIC explicit setting wins, and `false` means "no worklist here". Null
 * when nothing in the chain declares one, which is every card today.
 */
export function worklistForChain(chain = []) {
	for (let i = (chain || []).length - 1; i >= 0; i -= 1) {
		const setting = cardWorklistSetting(chain[i]);
		if (setting === undefined) continue;
		return setting === false ? null : setting;
	}
	return null;
}

/** The worklist config for one card id, walking its chain. */
export function cardWorklistFor(cardsById = {}, rawId) {
	return worklistForChain(cardChain(cardsById, rawId));
}

/**
 * A card's ancestor chain, parent-first, ending with the card itself. With
 * `elementSetsById` each node comes back RESOLVED (resolveCardNode): its
 * `elements` are the effective list, set elements included.
 */
export function cardChain(cardsById = {}, rawId, elementSetsById = null) {
	const chain = [];
	const seen = new Set();
	let id = normalizeId(rawId);
	while (id && !seen.has(id)) {
		seen.add(id);
		const card = hasOwn(cardsById, id) ? cardsById[id] : undefined;
		if (!card) break;
		chain.unshift(elementSetsById ? resolveCardNode(card, elementSetsById) : card);
		id = normalizeId(card?.parentCardId);
	}
	return chain;
}

/**
 * Build a resolver `cardNotesEnabled(cardId)` over an id index — the card-level
 * twin of createElementResolver, walking the same parent chain by the rule
 * above. An unknown card answers ON, the platform default.
 */
export function createNotesResolver(cardsById = {}) {
	const cache = new Map();
	return rawId => {
		const id = normalizeId(rawId);
		if (!id) return true;
		if (cache.has(id)) return cache.get(id);
		const enabled = notesEnabledForChain(cardChain(cardsById, id));
		cache.set(id, enabled);
		return enabled;
	};
}

/** Index a list of card definitions by normalized id. */
export function indexCardsById(cards = []) {
	return Object.fromEntries(
		(cards || [])
			.map(card => [normalizeId(card?._id), card])
			.filter(([id]) => Boolean(id))
	);
}

// ---- The primary date (docs/plans/primary-date.md; the ordered form in
// docs/plans/element-sets-and-me.md, "Review answers") ----------------------
//
// `cardDefinitions.options.primaryDate`: 'created' (the default — absent
// means created), 'updated', or the name of a date / date & time element the
// card defines, inherits or attaches through an element set — OR an ordered
// list of such names, resolved PER ITEM: the first entry whose stored value
// is populated, then the terminal ('created' always implied; 'updated' when
// it is named last). The default list order, the table's date column, the
// date on every item tile. Inherited like Notes: the most specific explicit
// setting wins whatever its shape, and a child may name a parent's element.

/** The sort token the API resolves per card (`sort_field=primaryDate`). */
export const PRIMARY_DATE_SORT = 'primaryDate';

export const DATE_ELEMENT_TYPES = ['date', 'date-time'];

/** A `date` or `date-time` element with a name — the only kinds a primary date may name. */
export const isDateElement = el => Boolean(el?.name) && DATE_ELEMENT_TYPES.includes(el?.dataType);

/** The two system tokens a setting may name; the sort fields they stand for. */
export const SYSTEM_PRIMARY_DATES = ['created', 'updated'];
export const isSystemPrimaryDate = token => SYSTEM_PRIMARY_DATES.includes(token);
/** The most entries an ordered list may carry (the API's MAX_ORDERED). */
export const PRIMARY_DATE_MAX_ORDERED = 8;

/**
 * The card's OWN setting, or undefined when it says nothing: a trimmed
 * string, or an ordered list of trimmed non-empty strings (blank entries
 * dropped; an emptied list says nothing) — read leniently, as the API reads
 * it; the write path refuses what this tolerates.
 */
export function cardPrimaryDateSetting(card) {
	const raw = card?.options?.primaryDate;
	if (typeof raw === 'string') {
		const value = raw.trim();
		return value === '' ? undefined : value;
	}
	if (Array.isArray(raw)) {
		const list = raw.filter(entry => typeof entry === 'string' && entry.trim() !== '').map(entry => entry.trim());
		return list.length ? list : undefined;
	}
	return undefined;
}

const CREATED_SPEC = Object.freeze({ kind: 'created', field: 'createdAt', name: null, label: 'Created', dataType: 'date-time', element: null });
const UPDATED_SPEC = Object.freeze({ kind: 'updated', field: 'updatedAt', name: null, label: 'Updated', dataType: 'date-time', element: null });
const systemSpec = token => (token === 'updated' ? UPDATED_SPEC : CREATED_SPEC);

const elementSpec = element => ({
	kind: 'element',
	field: element.name,
	name: element.name,
	label: element.label?.singular || element.name,
	dataType: element.dataType,
	element,
});

/**
 * "Scheduled, then Due, then Created" — an ordered spec in words; a plain
 * spec is its own label. The card home's fact, the sort menu's suffix, the
 * table header's tooltip.
 */
export function describePrimaryDate(spec) {
	if (!spec) return 'Created';
	if (spec.kind !== 'ordered') return spec.label || 'Created';
	return (spec.entries || []).map(entry => entry.label).join(', then ');
}

/** The ordered spec of a stored list, read leniently (see primaryDateForChain). */
function orderedSpec(list, pool) {
	const entries = [];
	const seen = new Set();
	let terminal = 'created';
	let explicitTerminal = false;
	list.forEach((token, i) => {
		if (isSystemPrimaryDate(token)) {
			// Only the LAST entry may be a token; one elsewhere is a stored
			// value the API's gate never admitted, skipped rather than
			// honoured out of place.
			if (i === list.length - 1) { terminal = token; explicitTerminal = true; }
			return;
		}
		if (seen.has(token)) return;
		seen.add(token);
		const element = pool.find(el => el?.name === token);
		if (isDateElement(element)) entries.push(elementSpec(element));
	});
	// No element survived: the list IS its terminal (a plain spec, so the
	// single-value readers and the createdAt fast path apply unchanged).
	if (!entries.length) return systemSpec(terminal);
	entries.push(systemSpec(terminal));
	const spec = { kind: 'ordered', field: null, name: null, dataType: null, element: null, entries, terminal, explicitTerminal };
	spec.label = describePrimaryDate(spec);
	return spec;
}

/**
 * What is the primary date of the card at the end of `chain` (parent-first,
 * ending with the card itself)? Returns a spec:
 *
 *   { kind: 'created' | 'updated' | 'element',
 *     field: 'createdAt' | 'updatedAt' | <element name>,   // what to read on an item, and the sort field
 *     name: <element name> | null,
 *     label, dataType: 'date' | 'date-time', element }     // the element def for kind 'element'
 *
 *   { kind: 'ordered', field: null, name: null, dataType: null, element: null,
 *     label: 'Scheduled, then Due, then Created',
 *     entries: [<element spec> …, <created | updated spec>],  // stored order, the terminal always last
 *     terminal: 'created' | 'updated', explicitTerminal }    // was the terminal named in the list?
 *
 * — a stored LIST, resolved per item by resolveItemPrimaryDate. A one-entry
 * list stays ordered (its blanks fall to the terminal), unlike the plain
 * string (a blank shows blank and sorts last).
 *
 * `elements` overrides the chain's merged element list — the editor passes
 * its unsaved elements under the names the API will give them. A setting
 * that names nothing the chain holds as a date reads as created, never
 * throws: the API refused it when it was chosen, so this is a card whose
 * element was removed or retyped since. In a list a stale entry is skipped,
 * and a list left with no element entries collapses to its terminal — the
 * API's lenient read, so the two agree on every card.
 */
export function primaryDateForChain(chain = [], elements = null) {
	let setting;
	for (let i = (chain || []).length - 1; i >= 0; i -= 1) {
		setting = cardPrimaryDateSetting(chain[i]);
		if (setting !== undefined) break;
	}
	if (setting === undefined || setting === 'created') return CREATED_SPEC;
	if (setting === 'updated') return UPDATED_SPEC;
	const pool = Array.isArray(elements) ? elements : mergeChainElements(chain);
	if (Array.isArray(setting)) return orderedSpec(setting, pool);
	const element = pool.find(el => el?.name === setting);
	if (!isDateElement(element)) return CREATED_SPEC;
	return elementSpec(element);
}

/**
 * The token the editor stores for a spec: the element's name, else the kind
 * — or, for an ordered spec, the list: the element names in order, then the
 * terminal when it was named out loud (an implied created is left implied).
 */
export const primaryDateToken = (spec) => {
	if (spec?.kind === 'ordered') {
		const names = (spec.entries || []).filter(entry => entry.kind === 'element').map(entry => entry.field);
		return spec.explicitTerminal ? [...names, spec.terminal] : names;
	}
	return spec?.kind === 'element' ? spec.field : (spec?.kind || 'created');
};

/**
 * Is this ONE stored entry a date? The API's rule (utils/cardPrimaryDate.js
 * dateText, the per-item reader; the sort key reads exactly the same values
 * as null through $dateFromString's onError): a Date, or text a date parses
 * from. Empty, another type, or text no date can be parsed from — a retyped
 * element's old value, say — is NOT populated, so an ordered list falls
 * through it and a tile never leads with it.
 */
const isDateEntry = (value) => {
	if (value instanceof Date) return !Number.isNaN(value.getTime());
	if (typeof value !== 'string' || value.trim() === '') return false;
	return !Number.isNaN(Date.parse(value));
};

/** A stored date value with a date in it: one parseable entry, or a list with at least one — undefined otherwise. */
const populatedDate = (dataType, raw) => {
	if (raw === undefined || raw === null || raw === '') return undefined;
	if (Array.isArray(raw)) {
		// A multi-value date counts by its EARLIEST entry — the `$min` rule
		// the API's sort and per-item reader key on, so tile and order agree
		// — with empties and non-dates ignored, as that `$min` ignores them.
		const list = raw.filter(isDateEntry);
		if (!list.length) return undefined;
		return earliestDateValue(dataType, list);
	}
	return isDateEntry(raw) ? raw : undefined;
};

/**
 * Which date does THIS item show, under a card's spec? The per-item rule:
 *
 *   created / updated  the item's timestamp (legacy spellings coalesced)
 *   element            the element's value as stored — undefined when blank;
 *                      the kind stays element, since a card that chose a
 *                      date shows a blank rather than a substitute
 *   ordered            the first entry whose value is populated, else the
 *                      terminal's timestamp
 *
 * Returns the ENTRY that supplied the value with `value` on it — { kind,
 * field, name, label, dataType, element, value } — never `ordered`: the
 * answer names the one date an item shows, which is what a tile says on
 * hover. Pure; safe on any item shape.
 */
export function resolveItemPrimaryDate(spec, item) {
	const s = spec && typeof spec === 'object' ? spec : CREATED_SPEC;
	if (s.kind === 'ordered') {
		for (const entry of (Array.isArray(s.entries) ? s.entries : [])) {
			if (entry.kind !== 'element') return { ...entry, value: getFieldValue(item, entry.field) };
			const value = populatedDate(entry.dataType, getFieldValue(item, entry.field));
			if (value !== undefined) return { ...entry, value };
		}
		return { ...CREATED_SPEC, value: getFieldValue(item, 'createdAt') };
	}
	if (s.kind === 'element') return { ...s, value: populatedDate(s.dataType, getFieldValue(item, s.field)) };
	const system = s.kind === 'updated' ? UPDATED_SPEC : CREATED_SPEC;
	return { ...system, value: getFieldValue(item, system.field) };
}

/**
 * Build a resolver `cardPrimaryDate(cardId)` over an id index — the
 * card-level twin of createNotesResolver, walking the same parent chain. An
 * unknown card answers created, the platform default.
 */
export function createPrimaryDateResolver(cardsById = {}, elementSetsById = null) {
	const cache = new Map();
	return rawId => {
		const id = normalizeId(rawId);
		if (!id) return CREATED_SPEC;
		if (cache.has(id)) return cache.get(id);
		// The chain resolved with its element sets: a set's date element may
		// be the card's primary date, exactly like an inherited one.
		const spec = primaryDateForChain(cardChain(cardsById, id, elementSetsById));
		cache.set(id, spec);
		return spec;
	};
}
